// One event layer for the public site.
//
//   track(name, props)   -> first-party events (our backend, always; anonymous ids, no PII)
//                         + GA4 / Google Ads / Meta Pixel (only after cookie consent)
//   trackLead(form, ...) -> conversion on every platform; call after a lead form succeeds
//   getAttribution()     -> snapshot to send WITH every lead form (lead source for the CRM)
//
// Never pass free text the visitor typed (messages, names, emails) as props.
import ReactPixel from 'react-facebook-pixel';
import { getToken } from './adminAuth';

const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/t`;
const DEFAULT_META_PIXEL = process.env.REACT_APP_META_PIXEL_ID || '526139164826297';
const SESSION_IDLE_MS = 30 * 60 * 1000;
const K = { vid: 'bcm_vid', sess: 'bcm_sess', first: 'bcm_first', consent: 'bcm_consent' };

const SEARCH_RE = /(^|\.)(google|bing|yahoo|duckduckgo|ecosia|baidu|yandex|startpage)\./i;
const SOCIAL_RE = /(facebook|fb\.|^fb$|instagram|^ig$|t\.co|twitter|x\.com|tiktok|reddit|linkedin|youtube|pinterest|snapchat|messenger|kijiji|marketplace)/i;

const store = {
  get(k, session) { try { return JSON.parse((session ? sessionStorage : localStorage).getItem(k)); } catch { return null; } },
  set(k, v, session) { try { (session ? sessionStorage : localStorage).setItem(k, JSON.stringify(v)); } catch { /* private mode */ } },
};
const rid = () => (window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);

const isLocal = () => /^(localhost|127\.|\[::1\])/.test(window.location.hostname);
export const trackingDisabled = () =>
  typeof window === 'undefined'
  || /ReactSnap/i.test(navigator.userAgent)            // build-time prerender
  || window.location.pathname.startsWith('/admin')
  || Boolean(getToken())                                 // staff browsing their own site
  || (isLocal() && !store.get('bcm_track_dev'));         // localStorage.bcm_track_dev = true to test locally

// ---------------------------------------------------------------- attribution

export function classifyChannel({ src = '', med = '', gclid = '', fbclid = '' }) {
  const s = src.toLowerCase(); const m = med.toLowerCase();
  const social = SOCIAL_RE.test(s); const search = SEARCH_RE.test(`${s}.`) || /^(google|bing|yahoo)$/.test(s);
  if (gclid || (/^(cpc|ppc|paid[-_]?search|sem)$/.test(m) && !social)) return 'Paid Search';
  if (/(paid|cpc|ppc|cpm|^ads?$)/.test(m) && social) return 'Paid Social';
  if (/(paid|cpc|ppc|cpm|display|^ads?$)/.test(m)) return 'Paid Other';
  if (m === 'email' || /newsletter|email/.test(s)) return 'Email';
  if (/^(sms|text)$/.test(m)) return 'SMS';
  if (m === 'organic' || (search && (!m || m === 'referral'))) return 'Organic Search';
  if (social || fbclid) return 'Organic Social';
  if (s && s !== '(direct)') return 'Referral';
  return 'Direct';
}

function readTouch() {
  const q = new URLSearchParams(window.location.search);
  const t = {
    src: q.get('utm_source') || '', med: q.get('utm_medium') || '', cmp: q.get('utm_campaign') || '',
    term: q.get('utm_term') || '', content: q.get('utm_content') || '',
    gclid: q.get('gclid') || q.get('gbraid') || q.get('wbraid') || '', fbclid: q.get('fbclid') || '',
    landing: window.location.pathname + window.location.search,
  };
  let refHost = '';
  try { refHost = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, '') : ''; } catch { /* bad referrer */ }
  const own = window.location.hostname.replace(/^www\./, '');
  if (refHost && refHost !== own) t.ref = refHost;
  if (!t.src) {
    if (t.gclid) { t.src = 'google'; t.med = 'cpc'; }
    else if (t.ref) { t.src = t.ref; t.med = SEARCH_RE.test(`${t.ref}.`) ? 'organic' : (SOCIAL_RE.test(t.ref) ? 'social' : 'referral'); }
    else if (t.fbclid) { t.src = 'facebook'; t.med = 'social'; }
  }
  t.ch = classifyChannel(t);
  if (!t.src) { t.src = '(direct)'; t.med = '(none)'; }
  return t;
}

const hasCampaign = (t) => Boolean(t.gclid || t.fbclid || t.cmp || (t.src && t.src !== '(direct)' && t.med !== '(none)'));

// Session = 30 min of inactivity, or arriving from a different campaign/referrer.
// URL params + referrer are read once per full page load (SPA navigations keep them).
let touchRead = false;
function session() {
  let vid = store.get(K.vid);
  if (!vid) { vid = rid(); store.set(K.vid, vid); }
  const now = Date.now();
  let s = store.get(K.sess);
  const touch = touchRead ? null : readTouch();
  touchRead = true;
  const idle = !s || now - s.last > SESSION_IDLE_MS;
  const newCampaign = touch && s && hasCampaign(touch)
    && (touch.src !== s.touch?.src || touch.cmp !== s.touch?.cmp || touch.gclid !== s.touch?.gclid);
  if (idle || newCampaign) {
    s = { sid: rid(), visit: (s?.visit || 0) + 1, touch: touch || readTouch() };
    if (!store.get(K.first)) store.set(K.first, s.touch);
  }
  s.last = now;
  store.set(K.sess, s);
  return { vid, ...s };
}

export function getAttribution() {
  if (typeof window === 'undefined') return null;
  flush();
  const s = session();
  return { sid: s.sid, vid: s.vid, visits: s.visit, ...s.touch, first: store.get(K.first) || undefined };
}

// ---------------------------------------------------------------- consent + ad tags

let cfg = { ga4: '', ads: '', adsLabel: '', meta: DEFAULT_META_PIXEL, banner: true };
let tagsLoaded = false;
const consentListeners = new Set();

export const getConsent = () => store.get(K.consent); // 'granted' | 'denied' | null
export const adsAllowed = () => getConsent() === 'granted' || (!cfg.banner && getConsent() !== 'denied');
export const needsConsentPrompt = () => cfg.banner && !getConsent();
export const onConsentChange = (fn) => { consentListeners.add(fn); return () => consentListeners.delete(fn); };

export function setConsent(granted) {
  store.set(K.consent, granted ? 'granted' : 'denied');
  if (granted) loadAdTags();
  else if (tagsLoaded) {
    window.gtag?.('consent', 'update', { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
    try { ReactPixel.revokeConsent(); } catch { /* not loaded */ }
  }
  consentListeners.forEach((fn) => fn());
}

// Called with site settings once they load (IDs are managed in Admin → Website Features).
export function configureAnalytics(settings = {}) {
  cfg = {
    ga4: (settings.ga4MeasurementId || '').trim(),
    ads: (settings.googleAdsId || '').trim(),
    adsLabel: (settings.googleAdsLeadLabel || '').trim(),
    meta: (settings.metaPixelId || '').trim() || DEFAULT_META_PIXEL,
    banner: settings.cookieBannerEnabled !== false,
  };
  if (adsAllowed()) loadAdTags();
  consentListeners.forEach((fn) => fn());
}

function loadAdTags() {
  if (tagsLoaded || trackingDisabled() || !adsAllowed()) return;
  tagsLoaded = true;
  const gid = cfg.ga4 || cfg.ads;
  if (gid) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); }; // eslint-disable-line prefer-rest-params
    window.gtag('js', new Date());
    window.gtag('consent', 'default', { analytics_storage: 'granted', ad_storage: 'granted', ad_user_data: 'granted', ad_personalization: 'granted' });
    if (cfg.ga4) window.gtag('config', cfg.ga4, { send_page_view: false });
    if (cfg.ads) window.gtag('config', cfg.ads);
    const el = document.createElement('script');
    el.async = true; el.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gid)}`;
    document.head.appendChild(el);
  }
  if (cfg.meta) {
    ReactPixel.init(cfg.meta, undefined, { autoConfig: true, debug: false });
    if (window.fbq) window.fbq.disablePushState = true; // we send PageView ourselves on route change
  }
  if (lastPage) forwardToAds('page_view', lastPage); // the page they consented on
}

// ---------------------------------------------------------------- events

let queue = [];
let timer = null;
let lastPage = null;

function flush() {
  if (timer) { clearTimeout(timer); timer = null; }
  if (!queue.length) return;
  const s = session();
  const body = JSON.stringify({ vid: s.vid, sid: s.sid, visit: s.visit, ctx: s.touch, ev: queue.splice(0, 25) });
  try {
    const sent = navigator.sendBeacon?.(ENDPOINT, new Blob([body], { type: 'text/plain' }));
    if (!sent) fetch(ENDPOINT, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain' } }).catch(() => {});
  } catch { /* tracking must never break the page */ }
  if (queue.length) flush();
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });
}

const vehicleItem = (v = {}) => ({
  id: v.id, title: v.title || [v.year, v.make, v.model].filter(Boolean).join(' '), price: Number(v.price) || 0,
  make: v.make, model: v.model, year: v.year, body: v.bodyType, cond: v.condition, cat: v.category,
});

function forwardToAds(name, props = {}) {
  if (!tagsLoaded) return;
  const g = window.gtag;
  try {
    switch (name) {
      case 'page_view':
        g?.('event', 'page_view', { page_path: props.path, page_location: window.location.href, page_title: document.title });
        ReactPixel.pageView();
        break;
      case 'vehicle_view':
      case 'segway_view':
        g?.('event', 'view_item', { currency: 'CAD', value: props.price || 0, items: [{ item_id: props.id, item_name: props.title, price: props.price || 0, item_brand: props.make, item_category: props.cat || props.body }] });
        ReactPixel.track('ViewContent', { content_ids: [props.id], content_name: props.title, content_type: 'product', value: props.price || 0, currency: 'CAD', vehicle_make: props.make, vehicle_model: props.model, vehicle_year: props.year });
        break;
      case 'search':
        g?.('event', 'search', { search_term: props.q });
        ReactPixel.track('Search', { search_string: props.q });
        break;
      case 'lead': {
        g?.('event', 'generate_lead', { form: props.form, currency: 'CAD', value: props.value || 0 });
        if (cfg.ads && cfg.adsLabel) g?.('event', 'conversion', { send_to: `${cfg.ads}/${cfg.adsLabel}` });
        const metaEvent = { financing: 'Lead', test_drive: 'Schedule', promo: 'Lead', exit_popup: 'Lead' }[props.form] || 'Contact';
        ReactPixel.track(metaEvent, { content_name: props.vehicle_title || props.form, content_category: props.form, content_ids: props.vehicle_id ? [props.vehicle_id] : undefined });
        break;
      }
      case 'click_call':
        g?.('event', 'click_to_call', { link_url: props.href, page_path: props.path });
        ReactPixel.trackCustom('ClickToCall', { page_path: props.path });
        break;
      default:
        g?.('event', name, props);
    }
  } catch { /* ad blockers etc. */ }
}

export function track(name, props = {}) {
  if (trackingDisabled()) return;
  const d = name === 'vehicle_view' || name === 'segway_view' ? vehicleItem(props) : props;
  queue.push({ n: name, p: window.location.pathname, d });
  if (name === 'lead' || queue.length >= 10) flush();
  else if (!timer) timer = setTimeout(flush, 2000);
  forwardToAds(name, { ...d, path: window.location.pathname });
}

export function trackPageView() {
  if (trackingDisabled()) return;
  lastPage = { path: window.location.pathname + window.location.search };
  track('page_view', lastPage);
}

// form: 'contact' | 'vehicle_inquiry' | 'financing' | 'test_drive' | 'exit_popup' | 'promo' | 'referral'
export const trackLead = (form, { vehicle_id, vehicle_title, value } = {}) =>
  track('lead', { form, vehicle_id, vehicle_title, value });

// One-shot per page/form: form_start fires the first time a visitor touches a lead form.
const started = new Set();
export function trackFormStart(form) {
  if (started.has(form)) return;
  started.add(form);
  track('form_start', { form });
}

// Delegated listener: every tel:/mailto:/sms:/Messenger/social link on the site, present or future.
export function installClickTracking() {
  if (typeof document === 'undefined' || installClickTracking.done) return;
  installClickTracking.done = true;
  document.addEventListener('click', (e) => {
    const a = e.target.closest?.('a[href]');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    const kind = href.startsWith('tel:') ? 'click_call'
      : href.startsWith('mailto:') ? 'click_email'
        : href.startsWith('sms:') ? 'click_sms'
          : /m\.me\//.test(href) ? 'click_messenger'
            : /(facebook|instagram|tiktok|youtube)\.com/.test(href) ? 'click_social' : null;
    // href/label are the dealership's own phone/email/page, never visitor data
    if (kind) track(kind, { href: href.split('?')[0], label: (a.textContent || '').trim().slice(0, 60) });
  }, { capture: true });
}
